import { z } from "zod";
import { deliveryNoteSchema } from "./delivery-note";
import { invoiceLayoutSchema } from "./invoice-layout";
import { invoicePaymentSchema } from "./invoice-payment";

const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Ungültiges Datum")
  .refine((value) => {
    const parsed = new Date(`${value}T12:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
  }, "Ungültiges Datum");

export const INVOICE_PARTS = ["A", "B", "C"] as const;
export const DEFAULT_INVOICE_PART_TITLES = {
  A: "Teil A – Förderfähige Leistungen",
  B: "Teil B – Zusatzleistungen",
  C: "Teil C – Eigenleistungen",
};
const invoicePartTitlesSchema = z.object({
  A: z.string().trim().min(1).max(150),
  B: z.string().trim().min(1).max(150),
  C: z.string().trim().min(1).max(150),
});

export const invoiceItemSchema = z.object({
  id: z.string().min(1).max(100),
  description: z.string().max(10000),
  quantity: z.number().finite().min(0).max(1e9),
  unit: z.string().max(40),
  unitPrice: z.number().finite().min(0).max(1e9),
  billable: z.boolean(),
  teil: z.enum(INVOICE_PARTS).optional(),
  // Offer photo labels can be the full damage description, not just a filename.
  sourceLabel: z.string().max(21000).optional(),
});

export const invoiceCoverPageSchema = z.object({
  customerNumber: z.string().max(100).default(""),
  orderNumber: z.string().max(300).default(""),
  projectReference: z.string().max(500).default(""),
  contactPerson: z.string().max(300).default(""),
  sellerDgNumber: z.string().max(100).default(""),
  introText: z.string().max(5000).default(""),
  additionalCompanyInfo: z.string().max(5000).default(""),
  paymentQr: invoicePaymentSchema.optional(),
  layout: invoiceLayoutSchema.optional(),
});

export type InvoiceCoverPage = z.infer<typeof invoiceCoverPageSchema>;
export const DEFAULT_INVOICE_COVER_PAGE: InvoiceCoverPage = {
  customerNumber: "", orderNumber: "", projectReference: "", contactPerson: "",
  sellerDgNumber: "", introText: "", additionalCompanyInfo: "",
};

export const invoiceDefaultsSchema = z.object({
  sellerUid: z.string().max(100),
  sellerDgNumber: z.string().max(100),
  additionalCompanyInfo: z.string().max(5000),
  paymentQr: invoicePaymentSchema.optional(),
});
export type InvoiceDefaults = z.infer<typeof invoiceDefaultsSchema>;

export const invoiceDataSchema = z.object({
  projectName: z.string().max(500),
  customerName: z.string().max(500),
  customerAddress: z.string().max(3000),
  customerUid: z.string().max(100),
  sellerUid: z.string().max(100),
  invoiceNumber: z.string().trim().max(100),
  invoiceDate: date,
  dueDate: z.union([date, z.literal("")]),
  servicePeriod: z.string().max(1000),
  sourceQuoteNumber: z.string().max(100),
  notes: z.string().max(20000),
  paymentTerms: z.string().max(5000),
  taxRate: z.number().finite().min(0).max(100),
  items: z.array(invoiceItemSchema).max(2000),
  groupByPart: z.boolean().optional(),
  partTitles: invoicePartTitlesSchema.optional(),
  deliveryNotes: z.array(deliveryNoteSchema).max(30).optional(),
  coverPage: invoiceCoverPageSchema.optional(),
});

export type InvoiceData = z.infer<typeof invoiceDataSchema>;
export type InvoiceItem = z.infer<typeof invoiceItemSchema>;

export interface InvoiceRecord {
  id: number;
  projectId: number;
  data: InvoiceData;
  createdAt: string | Date | null;
  updatedAt: string | Date | null;
}

export interface InvoicePhotoMetadata {
  id: number;
  invoiceId: number;
  fileName: string;
  displayName: string;
  description: string;
  mimeType: string;
  fileSize: number;
}

export const createInvoiceSchema = z.object({
  data: invoiceDataSchema,
  photoIds: z.array(z.number().int().positive()).max(500).default([]),
});

export const invoicePhotoDetailsSchema = z.object({
  // A copied title can be a legacy photo description; preserve it unchanged.
  displayName: z.string().trim().max(20000),
  description: z.string().max(20000),
});

/** Explain validation failures without exposing submitted values or internal JSON. */
export function invoiceValidationMessage(error: z.ZodError): string {
  const labels: Record<string, string> = {
    projectName: "Projektname", customerName: "Kundenname", customerAddress: "Kundenadresse",
    customerUid: "Kunden-UID", sellerUid: "Eigene UID", invoiceNumber: "Rechnungsnummer",
    invoiceDate: "Rechnungsdatum", dueDate: "Fälligkeitsdatum", servicePeriod: "Leistungszeitraum",
    sourceQuoteNumber: "Angebotsreferenz", notes: "Hinweise", paymentTerms: "Zahlungsbedingungen",
    taxRate: "Steuersatz", items: "Positionen", description: "Beschreibung", quantity: "Menge",
    unit: "Einheit", unitPrice: "Einzelpreis", billable: "Abrechnen", sourceLabel: "Fotoreferenz",
    teil: "Rechnungsteil", groupByPart: "Nach Teilen gruppieren", partTitles: "Teil-Überschriften",
    photoIds: "Ausgewählte Fotos", displayName: "Fototitel", fileName: "Dateiname",
    mimeType: "Bildformat", fileSize: "Dateigröße", data: "Rechnungsdaten",
    customerNumber: "Kundennummer", orderNumber: "Auftragsnummer", projectReference: "Bauvorhaben / Referenz",
    contactPerson: "Bearbeiter", sellerDgNumber: "Eigene DG-Nr.", introText: "Rechnungstext",
    additionalCompanyInfo: "Zusätzliche Firmen- und Bankangaben",
  };
  const details = error.issues.slice(0, 3).map((issue) => {
    const field = String(issue.path[issue.path.length - 1] ?? "data");
    const itemIndex = issue.path.indexOf("items");
    const position = itemIndex >= 0 && typeof issue.path[itemIndex + 1] === "number"
      ? ` (Position ${Number(issue.path[itemIndex + 1]) + 1})` : "";
    const label = (labels[field] ?? "Feld") + position;
    if (issue.code === "too_big" && issue.type === "string") {
      return `${label}: höchstens ${issue.maximum} Zeichen erlaubt`;
    }
    if (issue.code === "too_big" && issue.type === "array") {
      return `${label}: höchstens ${issue.maximum} Einträge erlaubt`;
    }
    if (issue.code === "invalid_type") {
      return `${label}: ${issue.expected === "number" ? "gültige Zahl" : "gültigen Wert"} angeben`;
    }
    if (issue.code === "too_small" && issue.type === "number") {
      return `${label}: muss mindestens ${issue.minimum} sein`;
    }
    if (issue.code === "too_big" && issue.type === "number") {
      return `${label}: darf höchstens ${issue.maximum} sein`;
    }
    return `${label}: ${issue.code === "custom" || issue.code === "invalid_string" ? issue.message : "ungültiger Wert"}`;
  });
  return `Ungültige Rechnungsdaten: ${details.join("; ")}.`;
}

function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

export function calculateInvoiceTotals(data: Pick<InvoiceData, "items" | "taxRate">) {
  const subtotal = roundMoney(data.items.reduce((sum, item) =>
    item.billable ? sum + roundMoney(item.quantity * item.unitPrice) : sum, 0));
  const tax = roundMoney(subtotal * data.taxRate / 100);
  return { subtotal, tax, total: roundMoney(subtotal + tax) };
}
